/**
 * -----------------------------------------------------------------------------
 * Geozone Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the form for editing the geozone details.
 */
import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AdminFormFieldAsyncDropdown,
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldSubmit,
  PillItem,
  PseudoSelect,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import {
  OVERLAP_PRIORITY_OPTIONS,
  ZONE_COLOR_OPTIONS,
  ZONE_TYPES_OPTIONS,
} from "./constants";
import { useLoggedInUserData } from "../../../utils/user";
import { useOrganizationGroupsQuery } from "../../../api/network/adminApiServices";
import { TLegacyGroup } from "../../../api/types/Group";
import { geozoneDetailsInitialValues } from "./validation";
import BasicMap, {
  MapLoadingAnimation,
} from "../../../components/maps/basicMap";
import {
  mapOperations as mapOperationsPolygon,
  mapUpdatesHandler as mapUpdatesHandlerPolygon,
} from "./map-polygon";
import {
  mapOperations as mapOperationsRoute,
  mapUpdatesHandler as mapUpdatesHandlerRoute,
} from "./map-route";
import {
  mapOperations as mapOperationsCircle,
  mapUpdatesHandler as mapUpdatesHandlerCircle,
} from "./map-circle";
import {
  TGeozoneMapData,
  TGeozoneMapDataCircle,
  TGeozoneMapDataForAPIs,
  TGeozoneMapDataPolygon,
  TGeozoneMapDataRoute,
  TLatLng,
  TMapState,
} from "../../../types/map";
import {
  TAutosuggestOptionValue,
  TMapOperations,
  TMapRef,
  TMapUpdatesHandler,
} from "./type";
import { useDispatch, useSelector } from "react-redux";
import { APP_CONFIG } from "../../../constants/constants";
import { useDebouncedCallback } from "use-debounce";
import { BingAutosuggestResItem } from "../../../api/types/Map";
import { geozoneDescriptionDisplayText } from "../../admins/geozones/geozones";
import { setMapStateData } from "../../../api/store/commonSlice";
import { getCircleLocs } from "../../../utils/map";
import {
  GeozoneType,
  GeozoneVehicleGroup,
  LegacyGeozoneType,
  TLegacyGeozone,
} from "../../../api/types/Geozone";
import { ColorRGB } from "../../../types/common";
import { useLocation } from "react-router-dom";
import {
  geozonePrepareMapDataForAPIs,
  geozonePrepareMapState,
  getGeozoneLocs,
} from "./util";
import { getGeozoneShapeCenter } from "../../../utils/geozone";
import { hexToRgb } from "../../../utils/common";
import svgIcons from "../../../assets/svg/svgIcons";
import { toast } from "react-toastify";
import GAutocompleteField from "../../map-fleet/gAutocompleteField";
import { useMapInit } from "../../../hooks/mapInit";
import MapTopRightControls from "../../../components/maps/mapTopRightControls";
import ModalLayerFilters from "../../map-fleet/modalLayerFilters";
import { TMapType, TMapLayerOptions } from "../../../types/map";
import { useGMapMode } from "../../../hooks/mapGoogle";
import {
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
} from "../account/constants";

const mapDimensionClass = "h-[500px]"; // h-96
const mapEditColDimensionClass = "max-h-[calc(500px-40px)] w-56";

export interface GeozoneDetailFormProps {
  values: typeof geozoneDetailsInitialValues;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean; // true if isFetchingSingleGeozone || !formikValuesReady
}

export const GeozoneDetailForm: FC<GeozoneDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.geozones.detailsPage.form",
  });
  const { t: tMap } = useTranslation("translation", {
    keyPrefix: "admins.geozones.detailsPage.map",
  });
  const { t: tMain } = useTranslation();
  const { state: locationState } = useLocation();
  const userCurrPos: TLatLng = useSelector(
    (state: any) => state.commonReducer.userCurrPos
  );
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const dispatch = useDispatch();
  const { onGAutocompletePlaceChanged } = useMapInit();

  // selected groups mechanism
  const [selectedGroups, setSelectedGroups] = React.useState<
    GeozoneVehicleGroup[]
  >([]);
  const [filteredGroupData, setFilteredGroupData] = React.useState<
    TSelectboxOption[]
  >([]);
  useEffect(() => {
    setSelectedGroups(!!values.groupid ? [values.groupid] : []);
    setFilteredGroupData(
      groupData.filter(
        (group) =>
          !selectedGroups.some(
            (selectedGroup: GeozoneVehicleGroup) =>
              selectedGroup === group.value
          )
      )
    );
  }, [values.groupid]);

  // prepare group query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgGroupsQueryParams, setOrgGroupsQueryParams] = React.useState({
    organization_id: `${thisUserOrganizationId}`,
    page: 1,
    page_size: 100,
    search: "",
  });

  // speed units
  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit)?.unit ||
    SPEED_UNITS_OPTIONS[0].unit;

  // fetch organization groups
  const { data: dataOrgGroups } =
    useOrganizationGroupsQuery(orgGroupsQueryParams);

  // prepare group data for display
  const { results } = dataOrgGroups ?? {};
  const groupData = !!results
    ? (results || ([] as TLegacyGroup[])).map(
        (item: TLegacyGroup, index: number) => ({
          value: item?.groupid,
          label: item?.displayname,
        })
      )
    : [];

  // update selected groups
  useEffect(() => {
    setFilteredGroupData(
      groupData.filter(
        (group) =>
          !selectedGroups.some(
            (selectedGroup: any) => selectedGroup.id === parseInt(group.value)
          )
      )
    );
    formikSetValue("assign_group", selectedGroups);
  }, [selectedGroups, dataOrgGroups]);

  // handle group selection
  const handleChangeGroup = (e: any) => {
    if (e?.value) {
      setSelectedGroups([
        ...selectedGroups,
        e.value,
        // {
        //   id: parseInt(e.value),
        //   name: e.label,
        //   organization: `${thisUserOrganizationId}`
        // }
      ]);
    }
  };

  // handle group removal
  const onRemoveFromGroup = (option: any) => {
    setSelectedGroups(
      selectedGroups.filter(
        (selectedGroup: GeozoneVehicleGroup) => selectedGroup !== option
      )
    );
    formikSetValue("assign_group", selectedGroups);
  };

  // common map ref to be used for various map operations
  const mapRef = React.useRef<TMapRef>({
    map: null,
    objects: {},
  });
  const { gMapGetMode, gMapSetMode } = useGMapMode();
  const [mapStateTransitionInProgress, setMapStateTransitionInProgress] =
    useState(false);

  const loadResetMapDataWithInitialValues = () => {
    if (APP_CONFIG.DEBUG.GEOZONES)
      console.log("[loadResetMapDataWithInitialValues]");
    const geozoneLocs = getGeozoneLocs(values as Partial<TLegacyGeozone>);
    const geozoneCenter = getGeozoneShapeCenter(
      values as Partial<TLegacyGeozone>,
      "object"
    ) as TLatLng;
    const mapDataFromSavedRecord = {
      //values.properties as TGeozoneMapData;
      centerPosition:
        // if computed center lat / lng is zero (happens with newly created route), then refer the first loc or default center
        geozoneCenter.latitude === 0 && geozoneCenter.longitude === 0
          ? geozoneLocs.length > 0
            ? geozoneLocs[0]
            : APP_CONFIG.MAPS.DEFAULTS.CENTER
          : geozoneCenter,
      locs: geozoneLocs,
      radius: values?.radius,
    };
    if (geozoneLocs.length > 0) {
      if (APP_CONFIG.DEBUG.GEOZONES) {
        console.log("==== SAVED MAP DATA ====");
        console.log(
          `ZONETYPE: ${ZONE_TYPES_OPTIONS.find((option) => option.value === `${values.zonetype}`)?.label}`
        );
        console.log(`LOCS: ${JSON.stringify(geozoneLocs.length ?? 0)}`);
        console.log(
          `CENTER: ${JSON.stringify(mapDataFromSavedRecord?.centerPosition)}`
        );
        console.log(
          `RADIUS: ${JSON.stringify(mapDataFromSavedRecord?.radius)}`
        );
        console.log(`COLOR: ${JSON.stringify(values.shapecolor)}`);
      }
      setMapStateTransitionInProgress(true);
      if (
        values.zonetype?.toString() === LegacyGeozoneType.POLYGON.toString()
      ) {
        setPolygonSides(
          `${geozoneLocs?.length ?? APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS}`
        );
      }
      const newMapState: TMapState = geozonePrepareMapState({
        seedMapState: mapState,
        shapeType: values.zonetype as LegacyGeozoneType,
        seedMapData: mapDataFromSavedRecord,
        centerPosition:
          mapDataFromSavedRecord?.centerPosition ??
          APP_CONFIG.MAPS.DEFAULTS.CENTER,
        polygonSides:
          (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs?.length ??
          APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS,
        locs:
          (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs ?? undefined,
        radius:
          (mapDataFromSavedRecord as TGeozoneMapDataCircle)?.radius ??
          APP_CONFIG.MAPS.DEFAULTS.RADIUS,
        color: hexToRgb(`${values.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
        editable: userCanEdit,
        ready: true,
      });
      if (APP_CONFIG.DEBUG.GEOZONES) console.log("newMapState", newMapState);
      setTimeout(() => {
        if (APP_CONFIG.DEBUG.GEOZONES)
          console.log(
            "[D] DISPATCH TO PRELOAD MAP STATE DATA FROM SAVED RECORD"
          );
        dispatch(setMapStateData(newMapState));
        setMapStateTransitionInProgress(false);
      }, 200);
    } else {
      console.log("NO SAVED MAP DATA");
    }
  };

  // a zone has map data if it has points in the latitude/longitude columns or in vertices
  const hasGeozoneMapData =
    getGeozoneLocs(values as Partial<TLegacyGeozone>).length > 0;

  // check map data
  useEffect(() => {
    if (!hasGeozoneMapData && !loadingData) {
      toast.error(tMap("data_incomplete"));
      // do not keep drawing the previously selected geozone
      dispatch(
        setMapStateData({ mapData: undefined, mapDataForAPIs: undefined })
      );
    }
  }, [hasGeozoneMapData, loadingData]);

  // set map data on form load
  useEffect(() => {
    // if(!!!values.properties || Object.keys(values.properties).length === 0 || loadingData) return;
    if (!hasGeozoneMapData || loadingData) {
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.log(
          `returns because of hasGeozoneMapData (${hasGeozoneMapData}) or loadingData = ${loadingData === true ? "true" : "false"}`
        );
      return;
    }
    loadResetMapDataWithInitialValues();
    // }, [values.properties, locationState?.new, loadingData]);
  }, [
    hasGeozoneMapData,
    values.latitude1,
    values.vertices,
    locationState?.new,
    loadingData,
  ]);

  const [isValidPlaceSelected, setIsValidPlaceSelected] = useState(
    !locationState?.new
  );

  // update map data on color change
  const handleGeozoneColorChange = (e: any) => {
    formikSetValue("shapecolor", e.value);
    if (!mapState) return;
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.log(
          "[D] DISPATCH TO UPDATE MAP STATE DATA ON COLOR CHANGE",
          e.value
        );
      dispatch(
        setMapStateData({
          ...geozonePrepareMapState({
            seedMapState: mapState,
            shapeType: values.zonetype as LegacyGeozoneType,
            seedMapData: mapState?.mapData ?? undefined,
            centerPosition:
              mapState.mapData?.centerPosition ??
              APP_CONFIG.MAPS.DEFAULTS.CENTER,
            polygonSides,
            locs: mapState?.mapData?.locs ?? undefined,
            radius: mapState?.mapData?.radius ?? undefined,
            color: hexToRgb(`${e.value}`), // APP_CONFIG.MAPS.COLORS[e?.value as keyof typeof APP_CONFIG.MAPS.COLORS],
            editable: userCanEdit,
          }),
        })
      );
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  // update map data on zone type change
  const handleGeozoneTypeChange = (e: any) => {
    formikSetValue("zonetype", e?.value);
    if (!mapState) return;
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      const shapeType = e?.value as LegacyGeozoneType;
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.log(
          "[D] DISPATCH TO UPDATE MAP STATE DATA ON ZONE TYPE CHANGE",
          "poly",
          polygonSides
        );
      const newMapState = geozonePrepareMapState({
        seedMapState: mapState,
        shapeType,
        seedMapData: undefined,
        centerPosition:
          mapState.mapData?.centerPosition ?? APP_CONFIG.MAPS.DEFAULTS.CENTER,
        polygonSides,
        locs: undefined,
        radius:
          shapeType?.toString() === LegacyGeozoneType.ROUTE.toString()
            ? APP_CONFIG.MAPS.DEFAULTS.RADIUS_CORRIDOR
            : mapState?.mapData?.radius || APP_CONFIG.MAPS.DEFAULTS.RADIUS,
        color: hexToRgb(`${values.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
        editable: userCanEdit,
      });
      if (APP_CONFIG.DEBUG.GEOZONES) console.log("newMapState", newMapState);
      dispatch(setMapStateData(newMapState));
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  useEffect(() => {
    if (APP_CONFIG.DEBUG.GEOZONES)
      console.log(
        "[D] DISPATCH TO UPDATE MAP STATE DATA ON USER CAN EDIT CHANGE"
      );
    dispatch(
      setMapStateData({
        // ...mapState,
        mapData: {
          ...mapState?.mapData,
          editable: userCanEdit,
        },
      })
    );
    const applicableMapUpdatesHandler =
      values.zonetype?.toString() === LegacyGeozoneType.ROUTE.toString()
        ? mapUpdatesHandlerRoute
        : values.zonetype?.toString() === LegacyGeozoneType.POLYGON.toString()
          ? mapUpdatesHandlerPolygon
          : mapUpdatesHandlerCircle;
    applicableMapUpdatesHandler(
      {
        mapRef,
        mapData: { ...mapState?.mapData }, // not in use
        setMapData: () => {}, // not in use
      },
      "edit",
      userCanEdit
    );
    if (userCanEdit) {
      setTimeout(() => {
        loadResetMapDataWithInitialValues();
      }, 100);
    }
  }, [userCanEdit]);

  // update map display when corridor radius changes
  const updateMapOnCorridorRadiusChange = () => {
    if (!mapState) return;
    const parsedRadius = parseFloat(`${values.radius}`);
    if (isNaN(parsedRadius) || parsedRadius <= 0 || parsedRadius > 65000)
      return;
    if (APP_CONFIG.DEBUG.GEOZONES)
      console.log(
        "[D] DISPATCH TO UPDATE MAP STATE DATA ON CORRIDOR RADIUS CHANGE"
      );
    setMapStateTransitionInProgress(true);
    dispatch(
      setMapStateData({
        ...geozonePrepareMapState({
          seedMapState: mapState,
          shapeType: values.zonetype as LegacyGeozoneType,
          seedMapData: mapState?.mapData ?? undefined,
          centerPosition:
            mapState.mapData?.centerPosition ?? APP_CONFIG.MAPS.DEFAULTS.CENTER,
          polygonSides,
          locs: mapState?.mapData?.locs ?? undefined,
          radius: parsedRadius,
          color: hexToRgb(`${values.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
          editable: userCanEdit,
        }),
      })
    );
    setTimeout(() => {
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  // carry out map operations on map ready
  const handleMapReady =
    // useCallback(
    () => {
      if (mapState?.mapData?.centerPosition?.latitude === 0) {
        console.error("MAP DATA ISSUE - lat = 0");
      } else {
        if (APP_CONFIG.DEBUG.GEOZONES) {
          console.log("==== Sending map operations ====");
          console.log(
            `ZONETYPE: ${ZONE_TYPES_OPTIONS.find((option) => option.value === `${values.zonetype}`)?.label}`
          );
          console.log(
            `LOCS: ${JSON.stringify((mapState?.mapData?.locs ?? []).length ?? 0)}`
          );
          console.log(
            `CENTER: ${JSON.stringify(mapState?.mapData?.centerPosition)}`
          );
          console.log(`RADIUS: ${JSON.stringify(mapState?.mapData?.radius)}`);
          console.log(`COLOR: ${JSON.stringify(mapState?.mapData?.color)}`);
        }
        const applicableMapOperations =
          values.zonetype?.toString() === LegacyGeozoneType.ROUTE.toString()
            ? mapOperationsRoute
            : values.zonetype?.toString() ===
                LegacyGeozoneType.POLYGON.toString()
              ? mapOperationsPolygon
              : mapOperationsCircle;
        applicableMapOperations({
          mapRef,
          mapData: { ...mapState?.mapData },
          setMapData: (newMapData: TGeozoneMapData) => {
            if (APP_CONFIG.DEBUG.GEOZONES)
              console.log(
                "[setMapData] via handleMapReady - dispatch(setMapStateData({...",
                JSON.stringify(mapState)
              );
            if (APP_CONFIG.DEBUG.GEOZONES)
              console.log(
                "[D] DISPATCH TO UPDATE MAP STATE DATA WHEN USER MAKES CHANGES ON THE MAP"
              );
            dispatch(
              setMapStateData(
                geozonePrepareMapState({
                  seedMapState: mapState,
                  shapeType: values.zonetype as LegacyGeozoneType,
                  seedMapData: newMapData,
                  centerPosition: newMapData.centerPosition,
                  polygonSides,
                  locs: newMapData.locs,
                  radius: newMapData.radius,
                  color: hexToRgb(`${values.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
                  editable: userCanEdit,
                })
              )
            );
          },
        });
      }
    };
  // , [mapState?.mapData, values.properties, userCanEdit]);

  const handleUpdateGeozoneOnNewlySelectedCoordinates = (
    lat: number,
    lng: number
  ) => {
    // update lat_lng field value
    formikSetValue("latitude1", lat);
    formikSetValue("longitude1", lng);

    // update map state
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.info(
          "[handleAutosuggestChange] Setting map data with new location coords",
          [lat, lng]
        );
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.log(
          "[D] DISPATCH TO UPDATE MAP STATE DATA ON LOCATION DROPDOWN CHANGE"
        );
      dispatch(
        setMapStateData({
          ...geozonePrepareMapState({
            seedMapState: mapState,
            shapeType: values.zonetype as LegacyGeozoneType,
            seedMapData: undefined,
            centerPosition: {
              latitude: lat,
              longitude: lng,
            },
            polygonSides,
            locs: undefined,
            radius: mapState?.mapData?.radius ?? undefined,
            color: hexToRgb(`${values.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
            editable: userCanEdit,
          }),
        })
      );
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  // shape controls
  const [polygonSides, setPolygonSides] = useState<string>(
    `${APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS}`
  );
  const handlePolygonSidesChange = (e: any) => {
    const newPolygonSides = e?.value;
    setPolygonSides(newPolygonSides);

    // update map state
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.info(
          "[handlePolygonSidesChange] Setting map data with new polygone sides value",
          newPolygonSides
        );
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.log(
          "[D] DISPATCH TO UPDATE MAP STATE DATA ON POLYGON SIDES CHANGE",
          e?.value
        );
      dispatch(
        setMapStateData({
          ...geozonePrepareMapState({
            seedMapState: mapState,
            shapeType: values.zonetype as LegacyGeozoneType,
            seedMapData: undefined,
            centerPosition: mapState.mapData?.centerPosition,
            polygonSides: newPolygonSides,
            locs: undefined,
            radius: mapState?.mapData?.radius ?? undefined,
            color: hexToRgb(`${values.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
            editable: userCanEdit,
          }),
        })
      );
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  // remove route point
  const removeRoutePoint = (pointIndex: number) => {
    // update map state
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.info(
          "[removeRoutePoint] Remove route point at index",
          pointIndex,
          typeof pointIndex
        );
      let newRouteLocs = [...(mapState.mapData?.locs ?? [])];
      if (newRouteLocs.length > 0) {
        newRouteLocs.splice(pointIndex, 1);
      }
      const newMapDataForAPIs: TGeozoneMapDataForAPIs = {
        ...mapState?.mapData,
        locs: [...newRouteLocs],
      };
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.log(
          "[D] DISPATCH TO UPDATE MAP STATE DATA ON ROUTE POINT REMOVE"
        );
      dispatch(
        setMapStateData({
          // ...mapState,
          mapData: {
            ...newMapDataForAPIs,
            color: hexToRgb(`${values.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
            ready: true,
            editable: userCanEdit,
          },
          mapDataForAPIs: newMapDataForAPIs,
        })
      );
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  const onGAutocompletePlaceChangeFinished: (
    place: google.maps.LatLng | undefined
  ) => void = (place) => {
    if (!!place?.lat() && !!place?.lng()) {
      setIsValidPlaceSelected(true);
      handleUpdateGeozoneOnNewlySelectedCoordinates(place?.lat(), place?.lng());
    } else {
      if (APP_CONFIG.DEBUG.MAPS) console.log("lat/lng empty", place);
    }
  };

  const handleMapTypeChange = (
    mapType: TMapType,
    latLng?: google.maps.LatLng
  ) => {
    if (APP_CONFIG.DEBUG.GEOZONES)
      console.log("[handleMapTypeChange]", mapType);

    const successCallback = () => {
      gMapSetMode(
        mapRef.current.map,
        mapType as TMapType,
        latLng || mapRef.current.map.getCenter()
      );
    };

    successCallback();
  };

  const handleMapLayerChange = (mapLayerOptions: TMapLayerOptions) => {
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      dispatch(
        setMapStateData({
          // ...mapState,
          mapLayerOptions,
        })
      );
      setMapStateTransitionInProgress(false);
    }, 100);
  };

  const handleMapCloseClick = () => {
    // Reset map to its default state
  };

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <div className="col-span-12 bg-field-error-dark/30 px-2 py-1 text-xs">
          <p>
            mapStateTransitionInProgress:{" "}
            {mapStateTransitionInProgress ? "true" : "false"}
          </p>
          <p>userCanEdit: {userCanEdit ? "true" : "false"}</p>
          <p>loadingData: {loadingData ? "true" : "false"}</p>
        </div>
      )}
      {!isValidPlaceSelected ? (
        <div className="col-span-12 h-125 flex flex-col items-center justify-center p-28 text-cnter br-gray-200 rounded">
          <h2 className="font-bold text-2xl text-gray-400">
            Select an address
          </h2>
          <p className="text-gray-400">
            {" "}
            Please enter a street address below to view the geozone on the map
          </p>
        </div>
      ) : mapState?.mapData?.ready &&
        !loadingData &&
        !mapStateTransitionInProgress ? (
        <div className="relative col-span-12 grid grid-cols-12">
          <BasicMap
            className={`col-span-12 bg-gray-200 ${mapDimensionClass}`}
            mapRef={mapRef}
            mapData={mapState?.mapData}
            setMapData={() => {}}
            onMapReady={handleMapReady}
          />
          <MapTopRightControls
            displayMode="satellite-with-zoom"
            mapRef={mapRef as React.MutableRefObject<TMapRef>}
          />
          <ModalLayerFilters
            displayMode="satellite-with-zoom"
            onMapTypeChange={handleMapTypeChange}
            onMapLayerChange={handleMapLayerChange}
            onMapCloseClick={handleMapCloseClick}
          />
          <div className="col-span-12 flex gap-4 mt-4">
            {userCanEdit && (
              <>
                {values.zonetype?.toString() ===
                  LegacyGeozoneType.CIRCLE.toString() && (
                  <div className="flex items-center gap-2">
                    <img src={svgIcons.MapMarkerBlue} alt="" />
                    <span className="font-bold text-sm text-[#5959FF]">
                      {t("bluePushpin")}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <img src={svgIcons.MapMarkerRed} alt="" />
                  <span className="font-bold text-sm text-[#FF3F4F]">
                    {t("redPushpin")}
                  </span>
                </div>
                <AdminFormFieldSubmit
                  customWrapperClass="col-span-6 ml-auto whitespace-nowrap"
                  type="button"
                  variant="primary-like"
                  label={tMap("reset_map")}
                  onClick={loadResetMapDataWithInitialValues}
                  disabled={loadingData}
                />
              </>
            )}
          </div>
          {userCanEdit && (
            <div
              className={`absolute flex flex-col ${mapEditColDimensionClass} top-4 left-4 z-[200] bg-white p-4 rounded-lg shadow text-sm`}
            >
              {/* ── CIRCLE ── */}
              {values.zonetype?.toString() ===
                LegacyGeozoneType.CIRCLE.toString() && (
                <>
                  <h4 className="font-bold text-lg mb-1">
                    {tMap("circle_radius")}
                  </h4>
                  <p className="text-[10px] leading-3 text-gray-400 mb-2">
                    {tMap("circle_radius_hint")}
                  </p>
                  <div className="flex">
                    <AdminFormFieldInput
                      label={tMap("radius_meters")}
                      type="text"
                      id="radius"
                      name="radius"
                      value={values.radius}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.radius}
                      touched={touched.radius}
                      disabled={!userCanEdit}
                      customWrapperClass="mt-2"
                    />
                    <AdminFormFieldSubmit
                      buttonDimensionClass="h-11 w-11 px-0 mt-6"
                      customWrapperClass="mt-2 flex items-start ml-2"
                      label={
                        <img
                          src={svgIcons.RefreshIcon}
                          className={`size-5 ${errors.radius || !userCanEdit ? "opacity-50 grayscale" : ""}`}
                        />
                      }
                      type="button"
                      variant="primary-like"
                      onClick={updateMapOnCorridorRadiusChange}
                      disabled={errors.radius || !userCanEdit}
                    />
                  </div>
                </>
              )}

              {/* <h4 className="font-bold text-lg mb-2">{tMap('shape_controls')}</h4> */}
              {values.zonetype?.toString() ===
                LegacyGeozoneType.POLYGON.toString() && (
                <>
                  <h4 className="font-bold text-lg mb-2">
                    {tMap("polygon_sides")}
                  </h4>
                  <AdminFormFieldDropdown
                    loadingData={loadingData}
                    label={false}
                    id="polygon_sides"
                    name="polygon_sides"
                    options={"*"
                      .repeat(
                        APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS_RANGE[1] -
                          APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS_RANGE[0] +
                          1
                      )
                      .split("")
                      .map(
                        (item, i) =>
                          i + APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS_RANGE[0]
                      )
                      .map((side) => ({
                        value: `${side}`,
                        label: `${side}`,
                      }))}
                    value={polygonSides}
                    onChange={handlePolygonSidesChange}
                  />
                </>
              )}
              {values.zonetype?.toString() ===
                LegacyGeozoneType.ROUTE.toString() && (
                <>
                  <h4 className="font-bold text-lg">{tMap("route_points")}</h4>
                  {!!mapState?.mapData?.locs?.length &&
                  mapState?.mapData?.locs?.length >=
                    APP_CONFIG.MAPS.DEFAULTS.ROUTE_POINTS_MAX ? (
                    <p className="text-[10px] leading-3 text-field-error-dark font-bold mb-2">
                      {tMap("route_points_max_reached", {
                        count: APP_CONFIG.MAPS.DEFAULTS.ROUTE_POINTS_MAX,
                      })}
                    </p>
                  ) : (
                    <p className="text-[10px] leading-3 text-gray-400 mb-2 max-w-40">
                      {tMap("create_route_point")}
                    </p>
                  )}
                  <div className={`flex-grow overflow-auto`}>
                    <div className="flex flex-col gap-2">
                      {!!mapState?.mapData?.locs?.length &&
                        mapState?.mapData?.locs?.length > 0 && (
                          <>
                            {mapState?.mapData?.locs.map((locItem, i) => (
                              <div
                                className="flex items-center bg-gray-50 rounded-lg gap-2 py-1 px-2"
                                key={`loc_${i}`}
                              >
                                <span className="font-semibold text-xs bg-gray-400 text-white px-1 rounded">
                                  {i + 1}
                                </span>
                                <span className="font-semibold text-xs text-gray-500 leading-4 tracking-tighter flex-grow text-center">
                                  {locItem.latitude.toFixed(5)},{" "}
                                  {locItem.longitude.toFixed(5)}
                                </span>
                                {userCanEdit && (
                                  <img
                                    src={svgIcons.CloseIcon}
                                    className="size-5 cursor-pointer opacity-80 hover:opacity-40"
                                    onClick={() => {
                                      removeRoutePoint(i);
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                          </>
                        )}
                    </div>
                  </div>
                  <div className="flex">
                    <AdminFormFieldInput
                      label={tMap("corridor_radius")}
                      bottomLabel={tMap("corridor_radius_bottom_label")}
                      type="text"
                      id="radius"
                      name="radius"
                      value={values.radius}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.radius}
                      touched={touched.radius}
                      disabled={!userCanEdit}
                      customWrapperClass="mt-2"
                    />
                    <AdminFormFieldSubmit
                      buttonDimensionClass="h-11 w-11 px-0 mt-6"
                      customWrapperClass="mt-2 flex items-start ml-2"
                      label={
                        <img
                          src={svgIcons.RefreshIcon}
                          className={`size-5 ${errors.radius || !userCanEdit ? "opacity-50 grayscale" : ""}`}
                        />
                      }
                      type="button"
                      variant="primary-like"
                      onClick={updateMapOnCorridorRadiusChange}
                      disabled={errors.radius || !userCanEdit}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className={`col-span-12 bg-gray-200 ${mapDimensionClass} flex items-center justify-center relative`}
          >
            <MapLoadingAnimation />
            {APP_CONFIG.DEBUG.MAPS && (
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 mt-8">
                <h4 className="font-bold text-lg text-black">
                  {!mapState?.mapData ||
                  Object.keys(mapState?.mapData).length < 2
                    ? tMain("maps.receiving_data")
                    : ""}
                </h4>
              </div>
            )}
          </div>
          <div className="col-span-12 flex gap-8 mb-4"></div>
        </>
      )}

      <AdminFormFieldInput
        customWrapperClass="col-span-4"
        label={t("geozoneid")}
        type="text"
        id="geozoneid"
        name="geozoneid"
        value={values.geozoneid}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.geozoneid}
        touched={touched.geozoneid}
        disabled={!userCanEdit}
        detailsFormField={true}
        readOnly={true}
      />

      {loadingData ? (
        <PseudoSelect
          customWrapperClass="col-span-8"
          label={t("streetaddress")}
        />
      ) : (
        <>
          <GAutocompleteField
            onPlaceChanged={(place: google.maps.places.PlaceResult) => {
              // set the field value
              formikSetValue("streetaddress", place?.formatted_address || "");
              // place changed callback to move the geozone to the new location
              onGAutocompletePlaceChanged(
                place,
                mapRef,
                onGAutocompletePlaceChangeFinished
              );
            }}
            onFieldClear={() => {
              formikSetValue("streetaddress", "");
              setIsValidPlaceSelected(false);
            }}
            fieldInAdminForm={true}
            AdminFieldOptions={{
              label: t("streetaddress"),
              fieldName: "streetaddress",
              values,
              handleChange,
              handleBlur,
              errors,
              touched,
              userCanEdit,
            }}
          />
          {/* <AdminFormFieldAsyncDropdown
            loadingData={loadingData || isFetchingAutosuggest}
            label={t("streetaddress")}
            id="streetaddress"
            name="streetaddress"
            noOptionsMessage={() => t("streetaddress_no_options")}
            defaultOption={{
              value: `${values.streetaddress}`,
              label: `${geozoneDescriptionDisplayText(`${values.streetaddress}`, "-")}`,
            }}
            loadOptions={debouncedLoadOptionsHandlerAutosuggest}
            onChange={handleAutosuggestChange}
            onBlur={(e) => {
              formikSetTouched("streetaddress", true);
              handleBlur(e);
            }}
            error={errors.streetaddress}
            touched={touched.streetaddress}
            disabled={
              !userCanEdit || loadingData || mapStateTransitionInProgress
            }
            detailsFormField={true}
            customWrapperClass="col-span-8"
            isClearable={true}
          /> */}
        </>
      )}

      <AdminFormFieldInput
        customWrapperClass="col-span-12"
        label={t("description")}
        type="text"
        id="description"
        name="description"
        value={values.description}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.description}
        touched={touched.description}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("zonetype")}
        id="zonetype"
        name="zonetype"
        value={`${values.zonetype}`}
        onChange={handleGeozoneTypeChange}
        onBlur={(e) => {
          formikSetTouched("zonetype", true);
          handleBlur(e);
        }}
        error={errors.zonetype}
        touched={touched.zonetype}
        options={ZONE_TYPES_OPTIONS}
        disabled={!userCanEdit || loadingData || mapStateTransitionInProgress}
        detailsFormField={true}
      />

      {/* <AdminFormFieldInput
        label={t("geocode")}
        type="text"
        id="geocode"
        name="geocode"
        value={values.geocode}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.geocode}
        touched={touched.geocode}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}

      {/* <AdminFormFieldInput
        label={t("lat_lng")}
        type="text"
        id="lat_lng"
        name="lat_lng"
        value={values.lat_lng}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.lat_lng}
        touched={touched.lat_lng}
        disabled={!userCanEdit}
        detailsFormField={true}
        readOnly={true}
      /> */}

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("priority")}
        id="priority"
        name="priority"
        value={`${values.priority}`}
        onChange={(e) => {
          formikSetValue("priority", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("priority", true);
          handleBlur(e);
        }}
        error={errors.priority}
        touched={touched.priority}
        options={OVERLAP_PRIORITY_OPTIONS}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      {/* ==== DISABLED TEMPORARILY ====
          ------------------------------
      <AdminFormFieldPillList disabled={!userCanEdit}>
        {selectedGroups.map((group, i) => (
          <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
            key={`pillItem_${i}`}
            disabled={!userCanEdit}
            item={group as PillItem}
            onRemove={() => onRemoveFromGroup(group)}
          />
        ))}
      </AdminFormFieldPillList>

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("assign_group")}
        id="assign_group"
        name="assign_group"
        value={filteredGroupData?.[0]?.value}
        onChange={handleChangeGroup}
        onBlur={(e) => {formikSetTouched("assign_group", true); handleBlur(e)}}
        error={errors.assign_group}
        touched={touched.assign_group}
        options={filteredGroupData}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-12"
        placeholder={t("assign_group_placeholder")}
      /> */}

      <AdminFormFieldCheckbox
        label={t("reversegeocode")}
        id="reversegeocode"
        type="checkbox"
        name="reversegeocode"
        checked={values.reversegeocode}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.reversegeocode}
        touched={touched.reversegeocode}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldCheckbox
        label={t("arrivalzone")}
        id="arrivalzone"
        type="checkbox"
        name="arrivalzone"
        checked={values.arrivalzone}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.arrivalzone}
        touched={touched.arrivalzone}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldCheckbox
        label={t("departurezone")}
        id="departurezone"
        type="checkbox"
        name="departurezone"
        checked={values.departurezone}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.departurezone}
        touched={touched.departurezone}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("shapecolor")}
        id="shapecolor"
        name="shapecolor"
        value={values.shapecolor}
        onChange={handleGeozoneColorChange}
        onBlur={(e) => {
          formikSetTouched("shapecolor", true);
          handleBlur(e);
        }}
        error={errors.shapecolor}
        touched={touched.shapecolor}
        options={ZONE_COLOR_OPTIONS}
        disabled={!userCanEdit || loadingData || mapStateTransitionInProgress}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={`${t("speedlimitkph")} (${SPEED_UNITS_OPTIONS?.find((item) => item.unit === (speedUnit as string))?.label || speedUnit})`}
        type="text"
        id="speedlimitkph"
        name="speedlimitkph"
        value={values.speedlimitkph}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.speedlimitkph}
        touched={touched.speedlimitkph}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
    </div>
  );
};
